import { MdKeyboardArrowDown, MdKeyboardArrowRight } from '@meronex/icons/md';
import { useUpdateEffect } from 'ahooks';
import React from 'react';
import AnimateHeight from 'react-animate-height';
import { Box, Button } from '@apia/theme';
import isPromise from 'is-promise';
import { IconType } from '@meronex/icons/lib';
import { isFunction, uniqueId } from 'lodash-es';
import {
  customEvents,
  disableChildrenFocus,
  enableChildrenFocus,
} from '../../utils/dom';
import persistentStorage from '../../utils/persistentStorage';

interface ICollapsiblePanel {
  children: React.ReactNode;
  className?: string;
  closedIcon?: (() => React.ReactElement) | IconType;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  iconSize?: number;
  id?: string;
  onClose?: () => unknown;
  onOpen?: (ev?: React.MouseEvent) => void | Promise<boolean>;
  openIcon?: (() => React.ReactElement) | IconType;
  label: React.ReactNode;
  titleButtons?: React.ReactNode;
}

const CollapsiblePanel = React.forwardRef<HTMLDivElement, ICollapsiblePanel>(
  (
    {
      children,
      className,
      closedIcon,
      collapsed,
      defaultCollapsed,
      iconSize = 27,
      id,
      onClose,
      onOpen,
      openIcon,
      label,
      titleButtons,
    },
    outerRef,
  ) => {
    const prefix = React.useMemo(() => uniqueId(), []);
    const actualId = `${prefix}_${id ?? 'collapsiblePanel'}`;

    const [isCollapsed, setIsCollapsed] = React.useState<boolean>(
      defaultCollapsed ??
        collapsed ??
        (persistentStorage[`collapsiblePanel${actualId}`] as boolean),
    );

    const [ref, setRef] = React.useState<HTMLDivElement | null>(null);

    const callOnOpen = React.useCallback(
      (updateState: unknown, ev?: React.MouseEvent) => {
        const onOpenResult = onOpen ? onOpen(ev) : null;
        if (isPromise(onOpenResult)) {
          void onOpenResult.then((result) => {
            if (result) if (updateState) setIsCollapsed((current) => !current);
          });
        } else if (updateState) setIsCollapsed((current) => !current);
      },
      [onOpen],
    );

    useUpdateEffect(() => {
      if (collapsed !== undefined) setIsCollapsed(collapsed);
      if (collapsed === false) callOnOpen(false);
    }, [collapsed]);

    useUpdateEffect(() => {
      if (isCollapsed) {
        if (onClose) onClose();
        persistentStorage[`collapsiblePanel${actualId}`] = true;
        if (ref)
          disableChildrenFocus(
            ref.querySelector('.collapsiblePanel__content') as HTMLElement,
          );
      } else {
        persistentStorage[`collapsiblePanel${actualId}`] = false;
        if (ref)
          enableChildrenFocus(
            ref.querySelector('.collapsiblePanel__content') as HTMLElement,
          );
      }
    }, [isCollapsed]);

    React.useEffect(() => {
      const handleCustomFocus = () => setIsCollapsed(false);
      ref
        ?.querySelector('.collapsiblePanel__content')
        ?.addEventListener(customEvents.focus, handleCustomFocus);
      return () => {
        ref
          ?.querySelector('.collapsiblePanel__content')
          ?.removeEventListener(customEvents.focus, handleCustomFocus);
      };
    }, [id, isCollapsed, onClose, onOpen, ref]);

    const ClosedIcon = React.useMemo(
      () => closedIcon ?? MdKeyboardArrowRight,
      [closedIcon],
    );
    const OpenIcon = React.useMemo(
      () => openIcon ?? MdKeyboardArrowDown,
      [openIcon],
    );

    return (
      <Box
        className={`collapsiblePanel ${className ?? ''}`}
        {...window.getVariant('layout.common.components.collapsiblePanel')}
        ref={(el: HTMLDivElement) => {
          if (isFunction(outerRef)) outerRef(el);
          setRef(el);
        }}
      >
        <Box className="collapsiblePanel__label">
          <Button
            {...window.getVariant('inherit')}
            aria-expanded={!isCollapsed}
            onClick={(ev) => callOnOpen(true, ev)}
            type="button"
            className="collapsiblePanel__label__button"
          >
            {isCollapsed ? (
              <ClosedIcon size={iconSize} />
            ) : (
              <OpenIcon size={iconSize} />
            )}
            {label}
          </Button>
          {titleButtons}
        </Box>
        <AnimateHeight
          id={actualId}
          duration={150}
          height={isCollapsed ? 0 : 'auto'}
          sx={{ '&>div': { display: 'block ' } }}
          aria-hidden={isCollapsed}
          className="collapsiblePanel__content"
        >
          {children}
        </AnimateHeight>
      </Box>
    );
  },
);

export default CollapsiblePanel;
